<template>
  <v-card flat>
    <v-stepper
      v-model="whatsAppActiveStep"
      vertical
      class="custom-header"
      non-linear
      flat
    >
      <v-form
        ref="formRef"
        class="multi-col-validation"
        @submit.prevent="handleSubmit"
      >
        <v-stepper-step
          editable
          :complete="whatsAppActiveStep > 1"
          step="1"
        >
          <div class="d-flex align-center">
            <span class="text--primary text-4xl font-weight-bold me-3">01</span>
            <div class="d-flex flex-column">
              <span class="text--primary text-sm font-weight-semibold">Provider Id</span>
              <span class="text--secondary text-xs">Selecione o provider Id do seu chatbot</span>
            </div>
          </div>
        </v-stepper-step>

        <v-stepper-content step="1">
          <v-col
            cols="12"
          >
            <v-select
              v-model="chatBotWhatsAppDataLocale.providerId"
              dense
              outlined
              label="Provider Id"
              item-text="nome"
              item-value="id"
              validate-on-blur
              :items="provedoresWhatsAppIds"
              :rules="[validators.select]"
            />
            <v-btn
              color="primary"
              class="mt-2"
              @click="whatsAppNextStep"
            >
              Próximo
            </v-btn>
          </v-col>
        </v-stepper-content>

        <v-stepper-step
          :complete="whatsAppActiveStep > 2"
          editable
          step="2"
        >
          <div class="d-flex align-center">
            <span class="text--primary text-4xl font-weight-bold me-3">02</span>
            <div class="d-flex flex-column">
              <span class="text--primary text-sm font-weight-semibold">Provider API Token</span>
              <span class="text--secondary text-xs">Digite o Provider API Token do seu chatbot</span>
            </div>
          </div>
        </v-stepper-step>

        <v-stepper-content step="2">
          <v-col
            cols="12"
          >
            <v-text-field
              v-model="chatBotWhatsAppDataLocale.providerAPIToken"
              dense
              outlined
              label="Provider API Token"
              hint="Máximo de 800 caracteres"
              validate-on-blur
              :rules="[validators.required, v => (!!v && v.length <= 800) || 'Máximo de 800 caracteres']"
            />
            <div class="mt-2">
              <v-btn
                outlined
                class="mr-2"
                @click="whatsAppPreviousStep"
              >
                Voltar
              </v-btn>
              <v-btn
                color="primary"
                @click="whatsAppNextStep"
              >
                Próximo
              </v-btn>
            </div>
          </v-col>
        </v-stepper-content>

        <v-stepper-step
          :complete="whatsAppActiveStep > 3"
          editable
          step="3"
        >
          <div class="d-flex align-center">
            <span class="text--primary text-4xl font-weight-bold me-3">03</span>
            <div class="d-flex flex-column">
              <span class="text--primary text-sm font-weight-semibold">Provider Identifier</span>
              <span class="text--secondary text-xs">Digite o Provider Identifier do seu chatbot</span>
            </div>
          </div>
        </v-stepper-step>

        <v-stepper-content step="3">
          <v-col
            cols="12"
          >
            <v-text-field
              v-model="chatBotWhatsAppDataLocale.providerIdentifier"
              dense
              outlined
              label="Provider Identifier"
              hint="Máximo de 300 caracteres"
              validate-on-blur
              :rules="[validators.required, v => (!!v && v.length <= 300) || 'Máximo de 300 caracteres']"
            />
            <div class="mt-2">
              <v-btn
                outlined
                class="mr-2"
                @click="whatsAppPreviousStep"
              >
                Voltar
              </v-btn>
              <v-btn
                color="primary"
                @click="whatsAppNextStep"
              >
                Próximo
              </v-btn>
            </div>
          </v-col>
        </v-stepper-content>

        <v-stepper-step
          :complete="whatsAppActiveStep > 4"
          editable
          step="4"
        >
          <div class="d-flex align-center">
            <span class="text--primary text-4xl font-weight-bold me-3">04</span>
            <div class="d-flex flex-column">
              <span class="text--primary text-sm font-weight-semibold">Alerta do envio de template</span>
              <span class="text--secondary text-xs">Ativar ou inativar</span>
            </div>
          </div>
        </v-stepper-step>

        <v-stepper-content step="4">
          <v-col cols="12">
            <v-switch
              v-model="chatBotWhatsAppDataLocale.permitirAlertaTemplate"
              class="mt-0"
              :label="`Alerta do envio de template: ${chatBotWhatsAppDataLocale.permitirAlertaTemplate ? 'Ativo' : 'Inativo'}`"
            />
            <v-btn
              outlined
              class="mt-2"
              @click="whatsAppPreviousStep"
            >
              Voltar
            </v-btn>
          </v-col>
        </v-stepper-content>

        <div class="d-flex justify-center flex-wrap py-2 my-2">
          <v-btn
            outlined
            @click="resetData"
          >
            Cancelar
          </v-btn>

          <v-btn
            color="success"
            class="ms-2"
            :loading="loading"
            type="submit"
          >
            {{ isChatBotWhatsAppNovo ? 'Criar' : 'Salvar' + ' WhatsApp' }}
          </v-btn>
        </div>
      </v-form>
    </v-stepper>
  </v-card>
</template>

<script>
import Vue from 'vue'
import { ref, watch, inject, computed } from '@vue/composition-api'
import { required, select } from '@core/utils/validation'
import CanalWhatsAppProviderEnum from '@/models/entities/canal/CanalWhatsAppProviderEnum'
import ChatBotService from '@/services/modules/ChatBotService'
import useForm from '@/composables/useForm'

export default {
  props: {
    chatBotId: {
      type: Number,
      required: true,
    },
  },
  setup(props, { emit }) {
    const provedoresWhatsAppIds = [CanalWhatsAppProviderEnum.Zenvia]

    const whatsAppActiveStep = ref(1)

    const chatBotWhatsAppData = inject('chatBotWhatsAppData')
    const chatBotWhatsAppDataLocale = ref({})
    watch(() => chatBotWhatsAppData.value, value => { chatBotWhatsAppDataLocale.value = value }, { immediate: true, deep: true })

    const isChatBotWhatsAppNovo = computed(() => !chatBotWhatsAppData.value.id)
    watch(() => isChatBotWhatsAppNovo.value, value => { if (!value) whatsAppActiveStep.value = 4 }, { immediate: true, deep: true })

    function getWhatsAppRequestBody() {
      return {
        providerAPIToken: chatBotWhatsAppData.value.providerAPIToken,
        providerIdentifier: chatBotWhatsAppData.value.providerIdentifier,
        providerId: chatBotWhatsAppData.value.providerId,
        permitirAlertaTemplate: chatBotWhatsAppDataLocale.value.permitirAlertaTemplate,
      }
    }

    const { formRef, loading, resetForm } = useForm()

    async function updateWhatsAppChatBot() {
      try {
        loading.value = true
        await ChatBotService.updateConfigWhatsApp(props.chatBotId, getWhatsAppRequestBody())
        emit('update:chat-bot-id', props.chatBotId)
        loading.value = false
      } catch {
        loading.value = false
      }
    }

    function resetData() {
      resetForm()
      emit('reset-data')
    }

    async function handleSubmit() {
      if (loading.value) return
      if (formRef.value.validate()) {
        updateWhatsAppChatBot()
      } else {
        Vue.$toast.warning('Preencha corretamente os campos antes de salvar')
      }
    }

    function whatsAppNextStep() {
      // eslint-disable-next-line no-plusplus
      whatsAppActiveStep.value++
    }

    function whatsAppPreviousStep() {
      // eslint-disable-next-line no-plusplus
      whatsAppActiveStep.value--
    }

    return {
      provedoresWhatsAppIds,
      chatBotWhatsAppDataLocale,
      loading,
      formRef,
      whatsAppActiveStep,
      isChatBotWhatsAppNovo,
      handleSubmit,
      resetData,
      whatsAppNextStep,
      whatsAppPreviousStep,
      validators: {
        required,
        select,
      },
    }
  },
}
</script>
